import styled from "styled-components";

import COLORS from "components/atoms/colors";
import { H5 } from "components/atoms/text";

import { getDate } from "utils/getDate";
import getParsedJSON from "utils/getParsedJSON";

import Address from "types/Address";
import Inquiry, { InquiryPins } from "types/Inquiry";

import useSort from "hooks/useSort";

import { ReactComponent as IconArrow } from "assets/images/common/arrow-down.svg";

interface Props {
  inquire: Inquiry;
}

const cols = ["60px", "*", "*", "*", "*", "*", "*", "*", "*", "*", "*"];

// 신청 내역 아이템
const HistoryItem = ({ inquire }: Props) => {
  // 신청내역 정렬
  const { handleSort, sortList } = useSort({
    list: inquire.inquiryPins,
  });

  const { receiver, address, contactNumber } = inquire;
  const addressObj = getParsedJSON<Address>(address);

  return (
    <div>
      <table className="mt-10 border-b border-white-60%">
        <Caption>
          {getDate({ date: inquire.createdAt, type: "YYYY.MM.DD HH:mm" })}
        </Caption>
        <colgroup>
          {cols.map((width, i) => (
            <col key={i} width={width} />
          ))}
        </colgroup>
        <THead>
          <tr>
            <Th>이미지</Th>
            <Th>
              <button
                onClick={() =>
                  handleSort({
                    sortKey: "pin.productOption.product.name",
                  })
                }
              >
                상품명
                <IconSort
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 18 18"
                />
              </button>
            </Th>
            <Th>
              <button
                onClick={() =>
                  handleSort({
                    sortKey: "pin.productOption.product.category.name",
                  })
                }
              >
                구분
                <IconSort
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 18 18"
                />
              </button>
            </Th>
            <Th>종류</Th>
            <Th>사이즈(W*L*T)</Th>
            <Th>
              <button
                onClick={() =>
                  handleSort({
                    sortKey: "pin.productOption.product.manufacturer",
                  })
                }
              >
                제조사
                <IconSort
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 18 18"
                />
              </button>
            </Th>
            <Th>원산지</Th>

            <Th>
              <button
                onClick={() =>
                  handleSort({
                    sortKey: "pin.productOption.price",
                  })
                }
              >
                가격
                <IconSort
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 18 18"
                />
              </button>
            </Th>
            <Th>프로젝트 - 위치</Th>
            <Th>면적(m2)</Th>
          </tr>
        </THead>
        <TBody>
          {sortList.map((inquirePin: InquiryPins) => {
            const {
              image,
              name,
              category,
              subCategory,
              manufacturer,
              country,
              origin,
            } = inquirePin.pin.productOption.product;

            const { size, price } = inquirePin.pin.productOption;

            const { scene, squareMeasure } = inquirePin.pin;

            return (
              <tr key={inquirePin.pinId}>
                <Td>
                  <div className="rounded-2 overflow-hidden w-[40px] h-[40px]">
                    <img src={image} alt="" className="w-full h-full" />
                  </div>
                </Td>
                <Td>{name}</Td>
                <Td>{category?.name}</Td>
                <Td>{subCategory}</Td>
                <Td>{size}</Td>
                <Td>{manufacturer}</Td>
                <Td>{origin === "수입산" ? country : origin}</Td>
                <Td>{price?.toLocaleString()}</Td>
                <Td>
                  {scene?.project?.name} - {scene?.title}
                </Td>
                <Td>{squareMeasure}</Td>
              </tr>
            );
          })}
        </TBody>
      </table>
      <div className="mt-4">
        <H5>배송지 정보</H5>
        <ul className="mt-4">
          <li className="flex gap-3 mb-3">
            <AddressTitle>수령인</AddressTitle>
            <p>{receiver}</p>
          </li>
          <li className="flex gap-3 mb-3">
            <AddressTitle>연락처</AddressTitle>
            <p>{contactNumber}</p>
          </li>
          <li className="flex gap-3 mb-3">
            <AddressTitle>배송지</AddressTitle>
            <div>
              <p>{addressObj?.postCode}</p>
              <p className="my-3">{addressObj?.baseAddress}</p>
              <p>{addressObj?.detailAddress}</p>
            </div>
          </li>
        </ul>
      </div>
      <Line />
    </div>
  );
};

const Caption = styled.caption`
  margin-bottom: 14px;
  text-align: left;
  font-size: 20px;
  font-weight: 700;
`;

const THead = styled.thead`
  border-bottom: 1px solid ${COLORS.black["40%"]};
`;

const Th = styled.th`
  padding: 6px 10px;
  color: ${COLORS.black["60%"]};
  font-weight: 400;

  & > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const TBody = styled.tbody`
  tr:first-child td {
    padding-top: 6px;
  }

  tr:last-child td {
    padding-bottom: 20px;
  }
`;

const Td = styled.td`
  text-align: center;
  padding: 2px 10px;
`;

const IconSort = styled(IconArrow)`
  width: 12px;
  height: 12px;
  path {
    fill: ${COLORS.black["60%"]};
  }
`;

const Line = styled.span`
  margin: 40px 0;
  display: block;
  width: 100%;
  height: 4px;
  background-color: ${COLORS.white["60%"]};
  border-top: 1px solid ${COLORS.black["20%"]};
`;

const AddressTitle = styled.p`
  color: ${COLORS.black["60%"]};
`;

export default HistoryItem;
